import { ec, ecId, ecLean, handBack, insertRoll, itemField, record, requiredInt, scalar, upper, type JanusCtx, type Row, definitionNotHeld } from '../context.js';
import { skipUnported } from '../../unported.js';
import { isContainer, moveApCost, stackTarget, type MoveRuleReaders } from '../../inventory/move-rules.js';
import { createVitalityReader } from '../../compile/vitality-reader.js';
import { statusItemId, statusRows } from '../../statuses/expiry.js';
import { inventoryCreationSizeTemplate } from '../../inventory/creation-size-template';
import { createInventoryTemplateComposer } from '../../inventory/compose-size-template';
import { readLayerListEntryUuid } from '../../compile/inventory-raw-readers';
import { emptySpawnPlan, mergeSpawnPlans } from '../../spawn/row-plan';
export { inventoryCreationSizeTemplate, inventoryCreationSizeTemplateIds } from '../../inventory/creation-size-template';

/** Raw storage proposal, committed atomically with the timed status and its frozen roll rows. */
export interface TimedInventoryDelta {
  updates: Array<{ inventory_id: string; before: Row; changes: Row }>;
  creates: Row[];
  removals: Array<{ status_id: string; target: string; item: string }>;
  logTargets?: string[];
}
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
export const inventoryValue = (ctx: JanusCtx, row: Row, key: string): unknown => scalar(ctx.read.inventoryField?.(row, key));
const delta = (ctx: JanusCtx): TimedInventoryDelta => ctx.timedInventory ??= { updates: [], creates: [], removals: [] };
/** Lean compile omits empty rosters; an unadopted base is still unreadable. */
function casterRoster(caster: Row): Row[] {
  if (caster._inventory_refusal === 'unported:inventory-base-pending') handBack('timed-finish:inventory-roster-not-held');
  if (caster.inventory === undefined) caster.inventory = [];
  if (!Array.isArray(caster.inventory)) handBack('timed-finish:inventory-roster-not-held');
  return caster.inventory as Row[];
}
/** Creation-time size fold; new template relations ride this grant's atomic commit. */
export function inventoryYieldTemplate(ctx: JanusCtx, caster: Row, def: Row, template: string, sizeId?: string): string {
  const type = String(itemField(def, 'item_type') ?? '');

  const sized = inventoryCreationSizeTemplate(type, sizeId ?? readLayerListEntryUuid(ctx.read.value(caster, 'size')));
  if (!template || !sized || upper(template) === sized) return upper(template || sized);
  const held = ctx.read.inventoryTemplates?.() ?? { enchantment_templates: [], enchantment_template_enchantments: [] };
  const pending = ctx.spawnRows?.writes ?? [], plan = emptySpawnPlan();
  const composer = createInventoryTemplateComposer({
    enchantment_templates: [...held.enchantment_templates, ...pending.filter(w => w.table === 'enchantment_templates').map(w => w.row)],
    enchantment_template_enchantments: [...held.enchantment_template_enchantments,
      ...pending.filter(w => w.table === 'enchantment_template_enchantments').map(w => w.row)],
  }, ctx.read.uuid, insert => plan.writes.push({ table: insert.kind === 'template' ? 'enchantment_templates' : 'enchantment_template_enchantments',
    op: 'insert', row: insert.kind === 'template' ? { ...insert.row, spawn_parent_id: ecId(caster) } : insert.row }));
  let id: string;
  try { id = composer.compose(template, sized); }
  catch { handBack('timed-finish:yield-template-not-held'); }
  if (plan.writes.length) ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
  return id;
}
export function inventoryUpdate(ctx: JanusCtx, row: Row, changes: Row, writes = delta(ctx)): void {
  const id = upper(inventoryValue(ctx, row, 'inventory_id'));
  if (!UUID.test(id)) handBack('timed-finish:inventory-identity-not-held');
  const prior = writes.updates.find(u => u.inventory_id === id);
  const before = Object.fromEntries(Object.keys(changes).filter(k => !prior || !(k in prior.before))
    .map(k => [k, inventoryValue(ctx, row, k) ?? (k === 'item_qty' ? 1 : null)]));
  if (prior) { Object.assign(prior.before, before); Object.assign(prior.changes, changes); }
  else writes.updates.push({ inventory_id: id, before, changes });
  const bucket = record(row.inventory);
  if (!bucket) handBack('timed-finish:inventory-row-not-held');
  Object.assign(bucket, changes);
}
export function consumeInventory(ctx: JanusCtx, source: Row, caster: Row): void {
  // Deletion of a container has spill/gravity duties. Do not silently strand its children.
  if (Array.isArray(source.inventory_in_container) && source.inventory_in_container.length) handBack('timed-finish:inventory-contents');
  const qty = Number(inventoryValue(ctx, source, 'item_qty') ?? 1);
  if (!Number.isSafeInteger(qty) || qty < 1 || Number(inventoryValue(ctx, source, 'inventory_deleted') ?? 0)) handBack('timed-finish:inventory-unavailable');
  const id = upper(inventoryValue(ctx, source, 'inventory_id'));
  inventoryUpdate(ctx, source, qty === 1 ? { inventory_deleted: 1, item_qty: 0 } : { item_qty: qty - 1 });
  insertRoll(ctx, { roll_type_1: 'inventory_deleted', roll_item_id: id, by: caster, to: source, roll_result: 1 });
}
function readers(ctx: JanusCtx): MoveRuleReaders {
  return { value: ctx.read.value, itemType: r => String(itemField(r ?? null, 'item_type') ?? ''),
    inventoryField: (r, k) => r ? inventoryValue(ctx, r, k) : null,
    statusItemIds: r => [...statusRows(r, 'target'), ...statusRows(r, 'caster')].map(statusItemId) };
}
/** Reuse find_stack_target's browser twin; duplicate recipe outputs see earlier provisional grants. */
export function grantInventory(ctx: JanusCtx, caster: Row, itemId: string, qty: number, template = '',
  creation?: { writes: TimedInventoryDelta; separate: boolean }): void {
  const writes = creation?.writes ?? delta(ctx);
  const def = ctx.read.definition(upper(itemId));
  if (!def) definitionNotHeld('timed-finish:yield-definition-not-held', itemId);
  if (!Number.isSafeInteger(qty) || qty < 1 || qty > 2147483647) handBack('timed-finish:yield-quantity');
  // Prestocked containers and randomized instance initialization require their own browser authoring lane.
  if (isContainer(def, readers(ctx)) || ['book', 'lock', 'vehicle_land', 'vehicle_water', 'vehicle_air'].includes(String(itemField(def, 'item_type')))
      || ['inventory', 'random_enchantment', 'equipment_packed', 'lockpick_difficulty', ...(creation ? [] : ['spoilage_int'])].some(k => ctx.read.has(def, k))) handBack('timed-finish:yield-initialization');
  if (template && !ctx.read.definition(upper(template))) handBack('timed-finish:yield-template-not-held');
  template = inventoryYieldTemplate(ctx, caster, def, template);
  const roster = casterRoster(caster);
  const possess = upper(record(caster.encounter_creatures)?.encounter_creature_id);
  const fields: Row = { inventory_item_id: upper(itemId), inventory_name: String(itemField(def, 'item_name') ?? ''),
    possess_id: possess, item_qty: qty, equipped: '', enchantment_template_id: template || null,
    inventory_private: 0, inventory_deleted: 0, inventory_wares: 0, x_pos: 0, y_pos: 0, z_pos: 0 };
  const provisional: Row = { ...def, inventory: fields };
  const candidates = roster.filter(r => {
    const update = writes.updates.find(u => u.inventory_id === upper(inventoryValue(ctx, r, 'inventory_id')));
    if (update) Object.assign(record(r.inventory)!, update.changes);
    return upper(inventoryValue(ctx, r, 'possess_id')) === possess;
  });
  const stack = creation?.separate ? { target: null } : stackTarget(provisional, candidates, { isFloor: false, x: 0, y: 0, z: 0 }, 0, readers(ctx));
  if ('unknown' in stack) handBack(`timed-finish:${stack.unknown}`);
  if (stack.target) {
    const target = candidates.find(r => upper(inventoryValue(ctx, r, 'inventory_id')) === stack.target)!;
    const next = Number(inventoryValue(ctx, target, 'item_qty') ?? 1) + qty;
    if (!Number.isSafeInteger(next) || next > 2147483647) handBack('timed-finish:yield-quantity');
    const created = writes.creates.find(r => r.inventory_id === stack.target);
    if (created) { created.item_qty = next; record(target.inventory)!.item_qty = next; }
    else inventoryUpdate(ctx, target, { item_qty: next }, writes);
  } else {
    const vitality = record(createVitalityReader({ resolveInventoryInstance: () => null })(def));
    fields.v_max = vitality?.v_max ?? 5;
    fields.v_current = vitality?.v_current ?? fields.v_max;
    fields.v_temp = vitality?.v_temp ?? 0;
    fields.v_max_temp = vitality?.v_max_temp ?? 0;
    fields.inventory_written_in_language = scalar(ctx.read.value(def, 'written_in_language')) ?? '';
    fields.inventory_id = ctx.read.uuid();
    writes.creates.push(fields);
    roster.push(provisional);
  }
  insertRoll(ctx, { roll_type_1: 'add_inventory', roll_item_id: upper(itemId), by: caster, to: caster, roll_result: qty });
}
export function collectInventory(ctx: JanusCtx, source: Row, caster: Row, integratedId: string): void {
  // Partial pickup must duplicate the instance's authored overrides/statuses, not re-mint its template.
  if (Number(inventoryValue(ctx, source, 'item_qty') ?? 1) !== 1) handBack('timed-finish:gathering:partial-world-stack');
  // Full move's light burndown, mounted generation, status cascades and child propagation need their own inventory transport.
  if (statusRows(source, 'target').some(s => statusItemId(s) !== integratedId && !Number(scalar(s.status_deleted) ?? 0))
      || inventoryValue(ctx, source, 'inventory_generation') || isContainer(source, readers(ctx))) handBack('timed-finish:gathering:pickup-side-effects');
  if (upper(inventoryValue(ctx, source, 'equipped'))) handBack('timed-finish:gathering:equipped-source');
  const candidates = casterRoster(caster).filter(r => upper(inventoryValue(ctx, r, 'possess_id')) === ecId(caster));
  const stack = stackTarget(source, candidates, { isFloor: false, x: 0, y: 0, z: 0 }, 0, readers(ctx));
  if ('unknown' in stack) handBack(`timed-finish:${stack.unknown}`);
  for (const status of statusRows(source, 'target')) {
    if (statusItemId(status) !== integratedId || Number(scalar(status.status_deleted) ?? 0)) continue;
    if (upper(status.status_connected_id)) handBack('timed-finish:gathering:integrated-connections');
    delta(ctx).removals.push({ status_id: upper(status.status_id), target: upper(inventoryValue(ctx, source, 'inventory_id')), item: integratedId });
    insertRoll(ctx, { roll_type_1: 'status_effect_removed', roll_item_id: integratedId, by: caster, to: source });
  }
  if (Number(ctx.read.value(ctx.encounter, 'in_combat') ?? 0) === 1) {
    const cost = moveApCost('move_inventory', source, readers(ctx));
    const before = requiredInt(ecLean(caster, 'aps'), 'timed-finish:gathering:aps-not-held');
    // Legacy gathering succeeded before pickup was attempted; preserve its roll and Integrated removal.
    if (before < cost) {
      const name = String(ecLean(caster, 'encounter_creature_name') ?? itemField(caster, 'item_name') ?? 'The acting creature');
      if (!ctx.dryRun) ctx.rows.push({ ...skipUnported(`${name}: gathering succeeded; pickup skipped: In Combat: This action requires ${cost} AP!`),
        roll_by: ecId(caster), roll_to: ecId(caster), roll_item_id: upper(inventoryValue(ctx, source, 'inventory_id')) });
      return;
    }
    ec(caster).aps = before - cost;
  }
  if (stack.target) {
    const target = candidates.find(r => upper(inventoryValue(ctx, r, 'inventory_id')) === stack.target)!;
    const next = Number(inventoryValue(ctx, target, 'item_qty') ?? 1) + 1;
    if (!Number.isSafeInteger(next) || next > 2147483647) handBack('timed-finish:yield-quantity');
    inventoryUpdate(ctx, target, { item_qty: next });
    inventoryUpdate(ctx, source, { inventory_deleted: 1, item_qty: 0 });
  } else inventoryUpdate(ctx, source, { possess_id: ecId(caster), equipped: '', inventory_wares: 0, x_pos: 0, y_pos: 0, z_pos: 0 });
  insertRoll(ctx, { roll_type_1: 'move_inventory', roll_item_id: upper(inventoryValue(ctx, source, 'inventory_id')), by: caster, to: caster, roll_result: 1 });
}
